import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { CallIntelConstant, CallIntelTypes } from '@frontend/api-call-intel';
import { callStatusUtils } from '@frontend/call-intel-core';

export const isAnalyzedCall = (record: HydratedCallRecord): boolean =>
  !!record.callIntelInsights?.id &&
  !callStatusUtils.isSkippedCall(record?.callIntelInsights.status) &&
  !callStatusUtils.isFailedCall(record?.callIntelInsights.status) &&
  record.status === 'answered';

export const hasPatientContact = (record: HydratedCallRecord): boolean =>
  !!(record.personId || record.person?.personId || record.callIntelInsights?.person?.id);

export const findBestEligibleCallIdx = (records: HydratedCallRecord[]): number => {
  let firstAnalyzedIdx = -1;
  for (let i = 0; i < records.length; i++) {
    if (!isAnalyzedCall(records[i])) continue;
    if (hasPatientContact(records[i])) return i;
    if (firstAnalyzedIdx === -1) firstAnalyzedIdx = i;
  }
  return firstAnalyzedIdx;
};

export const findBestEligibleCall = <T extends HydratedCallRecord>(records: T[]): T | undefined => {
  const idx = findBestEligibleCallIdx(records);
  return idx === -1 ? undefined : records[idx];
};

export const findBestEligibleCallWithContact = <T extends HydratedCallRecord>(records: T[]): T | undefined =>
  records.find((record) => isAnalyzedCall(record) && hasPatientContact(record));

export const isProEligibleAnalyzedCall = (call: CallIntelTypes.CallRecord): boolean => {
  const { isFailedCall, isSkippedCall, isProcessingCall } = callStatusUtils.getCallFlags(call);

  return (
    !!call.id &&
    !isProcessingCall &&
    !isFailedCall &&
    !isSkippedCall &&
    call.status === CallIntelTypes.CallStatusEnum.CALL_STATUS_COMPLETE
  );
};

export const hasSavedContact = (call: CallIntelTypes.CallRecord): boolean => {
  return !!call.person?.id;
};

export const findFirstProEligibleAnalyzedCall = (
  calls: CallIntelTypes.CallRecord[]
): CallIntelTypes.CallRecord | undefined => {
  return calls.find(isProEligibleAnalyzedCall);
};

export const isProNonPatientAnalyzedCall = (call: CallIntelTypes.CallRecord): boolean => {
  const contactType = call?.contactType;

  return (
    isProEligibleAnalyzedCall(call) &&
    !!contactType &&
    CallIntelConstant.NON_PATIENT_CONTACT_TYPES.includes(contactType)
  );
};

export const findFirstProEligiblePatientContextCall = (
  calls: CallIntelTypes.CallRecord[]
): CallIntelTypes.CallRecord | undefined => {
  return calls.find((call) => isProEligibleAnalyzedCall(call) && hasSavedContact(call));
};

export const findProEligibleCallTargets = (
  calls: CallIntelTypes.CallRecord[]
): {
  firstEligibleAnalyzedCall?: CallIntelTypes.CallRecord;
  firstEligiblePatientAnalyzedCall?: CallIntelTypes.CallRecord;
} => {
  let firstEligibleAnalyzedCall: CallIntelTypes.CallRecord | undefined;
  let firstEligiblePatientAnalyzedCall: CallIntelTypes.CallRecord | undefined;

  for (const call of calls) {
    if (!isProEligibleAnalyzedCall(call)) continue;

    firstEligibleAnalyzedCall ??= call;

    const contactType = call.contactType;
    const isNonPatientCall = !!contactType && CallIntelConstant.NON_PATIENT_CONTACT_TYPES.includes(contactType);

    if (!isNonPatientCall) {
      firstEligiblePatientAnalyzedCall = call;
    }

    if (firstEligibleAnalyzedCall && firstEligiblePatientAnalyzedCall) {
      break;
    }
  }

  return {
    firstEligibleAnalyzedCall,
    firstEligiblePatientAnalyzedCall,
  };
};
